import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '@/lib/tw-merge/cn';

interface MarkdownTextProps {
  /** Raw message text. May contain markdown — `**bold**`, lists, links, etc. */
  content: string;
  /** Optional class for the outer wrapper (controls font size / colour). */
  className?: string;
}

/**
 * Renders chat message content as markdown with chat-appropriate
 * defaults. Agents sometimes reply with formatting (bullet lists,
 * bold field names, etc.) — rendering as plain text leaves the
 * literal `**` and `-` characters in the bubble. Drop this in
 * wherever an agent message is shown.
 */
export default function MarkdownText({
  content,
  className,
}: MarkdownTextProps) {
  return (
    <div className={cn('chat-md break-words', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: props => (
            <p className="mb-2 leading-[1.45] last:mb-0">{props.children}</p>
          ),
          strong: props => (
            <strong className="font-semibold">{props.children}</strong>
          ),
          em: props => <em className="italic">{props.children}</em>,
          code: props => (
            <code className="rounded bg-black/10 px-1 py-0.5 font-mono text-xs">
              {props.children}
            </code>
          ),
          pre: props => (
            <pre className="mt-2 overflow-x-auto rounded bg-black/5 p-2 font-mono text-xs">
              {props.children}
            </pre>
          ),
          ul: props => (
            <ul className="my-2 list-disc space-y-1 pl-5 leading-[1.45]">
              {props.children}
            </ul>
          ),
          ol: props => (
            <ol className="my-2 list-decimal space-y-1 pl-5 leading-[1.45]">
              {props.children}
            </ol>
          ),
          li: props => (
            <li className="pl-1 leading-[1.45] [&>p]:mb-0">{props.children}</li>
          ),
          a: props => (
            <a
              href={props.href}
              className="underline hover:opacity-80"
              target="_blank"
              rel="noopener noreferrer"
            >
              {props.children}
            </a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
